export type ConnectionPage<TEdge> = {
  edges: TEdge[];
  pageInfo: { hasNextPage: boolean; endCursor?: string | null };
};

/**
 * Reads a cursor-paginated GraphQL connection to its end. `fetchPage` is called
 * with the previous page's `endCursor` until `hasNextPage` is false. Resolves
 * to undefined as soon as a page has no connection, so callers can tell a
 * missing parent apart from an empty list. Throws when a page returns a cursor
 * that was already requested, since following it would loop forever and
 * stopping would return a partial list that looks complete.
 */
export const fetchAllEdges = async <TEdge>(
  fetchPage: (after?: string) => Promise<ConnectionPage<TEdge> | undefined>,
): Promise<TEdge[] | undefined> => {
  const edges: TEdge[] = [];
  const requestedCursors = new Set<string>();
  let after: string | undefined;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop -- each page's cursor comes from the previous response
    const page = await fetchPage(after);
    if (!page) {
      return undefined;
    }
    edges.push(...page.edges);

    const { hasNextPage, endCursor } = page.pageInfo;
    if (!hasNextPage || !endCursor) {
      return edges;
    }
    if (requestedCursors.has(endCursor)) {
      throw new Error(`Page cursor ${endCursor} was repeated`);
    }
    requestedCursors.add(endCursor);
    after = endCursor;
  }
};
